import {Card} from "../card/card.jsx";
import React, {useCallback, useEffect, useState} from "react";
import {useQuery} from "@tanstack/react-query";
import placeService from "../../services/web/place-service.js";
import SelectComponent from "../select/select-component.jsx";
import locationService from "../../services/web/location-service.js";
import InputForm from "../input/input-form.jsx";
import Button from "../button/button.jsx";
import {getCities, getNeighborhoods, getStreets} from "../../utils/location-utils.js";
import optionsUtils from "../../utils/options-utils.js";
import Loader from "../template/loader.jsx";

import SentryRow from "../template/sentry-row.jsx";
import ModalReverseGeocoding from "../modal/modal-reverse-geocoding.jsx";

const REFERENCE_QUERY_OPTIONS = {staleTime: Infinity, gcTime: 1000 * 60 * 60};

const RegisteredLocation = ({
        cardClassName,
        title = "Endereço",
        showTitle = true,
        actionCopy = null,
        actionInfo = null,
        data,
        setData,
        corporation,
        showSectors = false,
        storePlaceName = false,
        showCoordinates = true,
        coordinatesRequired = true,
        showMap = true,
        showConfirmLocationAndAddress = true,
        disabled = false
    }) => {
    const [showModal, setShowModal] = useState(false);
    const [isLoadingReverseGeocoding, setIsLoadingReverseGeocoding] = useState(false);
    const [placeIdToHydrate, setPlaceIdToHydrate] = useState(null);

    const getOptionByValue = (options, value) => {
        if (value === null || value === undefined || value === "") return null;
        if (typeof value === "object" && value.value !== undefined) return value;

        return options.find(option => String(option.value) === String(value))
            ?? {value, label: value};
    };

    const parseValues = (value) => {
        if (!value) return [];
        if (Array.isArray(value)) return value;

        if (typeof value === "string") {
            try {
                const parsed = JSON.parse(value);
                if (Array.isArray(parsed)) return parsed;
            } catch {
                return value.includes("|") ? value.split("|").map(item => item.trim()) : [value];
            }
        }

        return [value];
    };

    const getMultiOptionsByValue = (options, value) => parseValues(value)
        .map(item => getOptionByValue(options, item?.value ?? item?.id ?? item?.name ?? item?.text ?? item))
        .filter(Boolean);

    const getValueByOption = (item) => item?.value ?? item?.id ?? item?.name ?? item?.text ?? item;

    const getValuesByOption = (value) => parseValues(value)
        .map(item => getValueByOption(item))
        .filter(item => item !== null && item !== undefined && item !== "");

    const getDisplayValue = (value) => parseValues(value)
        .map(item => item?.label ?? item?.text ?? item?.name ?? item?.value ?? item)
        .filter(item => item !== null && item !== undefined && item !== "")
        .join(", ");

    const getSelectedPlace = (places, place, placeId) => {
        if (storePlaceName) {
            return places.find(address => String(address.value) === String(placeId) || address.label === place) ?? null;
        }

        return places.find(address => String(address.value) === String(place)) ?? null;
    };

    const cityBuilder = useCallback((city, state) => {
        if (city && state) return `${city} - ${state}`
        if (city && !state) return city;
        return ""
    }, []);

    const {data: registeredAddress = []} = useQuery({
        queryFn: placeService.allPlaces,
        select: places => places.map(place => ({value: place.id, label: place.text})),
        queryKey: ["registeredPlaces"],
        ...REFERENCE_QUERY_OPTIONS,
    });

    const {data: types = [], isLoading: isLoadingTypes} = useQuery({
        queryFn: placeService.getTypes,
        select: types => types.map(type => ({value: type.id, label: type.text})),
        queryKey: ["types"],
        ...REFERENCE_QUERY_OPTIONS,
    });

    const {data: sectorOptions = [], isLoading: isLoadingSectors} = useQuery({
        queryFn: placeService.dispatchSectors,
        queryKey: ["address-sector"],
        enabled: !!showSectors,
        ...REFERENCE_QUERY_OPTIONS,
    });

    const selectedPlace = getSelectedPlace(registeredAddress, data.place, data.placeId);
    const selectedPlaceId = selectedPlace?.value ?? (!storePlaceName ? data.place : null);
    const shouldHydrateSelectedPlace = placeIdToHydrate !== null
        && String(placeIdToHydrate) === String(selectedPlaceId);
    const selectedType = types.find(address => String(address.value) === String(data.type_place)) ?? null;
    const selectedSectors = getMultiOptionsByValue(sectorOptions, data.sectors);

    const {data: placeData = null, isLoading: isLoadingPlaceData} = useQuery({
        queryFn: () => showSectors ? locationService.getDispatchLocationById(selectedPlaceId) : locationService.getLocationById(selectedPlaceId),
        queryKey: ["place-data", selectedPlaceId],
        enabled: !!selectedPlaceId && shouldHydrateSelectedPlace,
        ...REFERENCE_QUERY_OPTIONS,
    });

    const handleManualAddressChange = (key, value, clearPlace = true) => {
        if (clearPlace && data.place) setPlaceIdToHydrate(null);

        setData(prev => {
            const newData = {...prev, [key]: value};
            if (clearPlace && prev.place) {
                newData.place = "";
                newData.placeId = "";
            }
            return newData;
        });
    };

    const handleRegisteredPlaceChange = (option) => {
        setPlaceIdToHydrate(option?.value ?? null);
        setData(prev => ({
            ...prev,
            place: storePlaceName ? option?.label ?? "" : option?.value ?? "",
            placeId: storePlaceName ? option?.value ?? "" : undefined,
        }));
    };

    const confirmLocation = (address) => {
        setPlaceIdToHydrate(null);
        setData(prev => ({
            ...prev,
            place: "",
            placeId: "",
            type_place: "",
            street: address?.street ?? "",
            number: address?.number ?? "",
            neighborhood: address?.neighborhood ?? "",
            city: cityBuilder(address?.city, address?.district),
            latitude: address?.latitude ?? "",
            longitude: address?.longitude ?? "",
            crossroads: "",
            reference_point: "",
            sectors: showSectors ? getValuesByOption(address?.sector) : prev.sectors,
        }));
    };

    useEffect(() => {
        if (!placeData || isLoadingTypes || !shouldHydrateSelectedPlace) return;

        const defaultData = {
            neighborhood: placeData.neighborhood ?? "",
            latitude: placeData.latitude ?? "",
            longitude: placeData.longitude ?? "",
            city: cityBuilder(placeData.city, placeData.state),
            street: placeData.street ?? "",
            type_place: types.find(el => el.label === placeData.type || el.value === placeData.type)?.value ?? placeData.type ?? "",
            number: placeData.number,
        }

        if (placeData.crossroads) defaultData.crossroads = placeData.crossroads;
        if (placeData.reference_point) defaultData.reference_point = placeData.reference_point ?? placeData.referencePoint ?? "";

        setData(prev => ({
            ...prev,
            ...defaultData,
            sectors: showSectors ? getValuesByOption(placeData.sector ?? placeData.sectors) : prev.sectors,
        }));
        setPlaceIdToHydrate(null);
    }, [cityBuilder, isLoadingTypes, placeData, shouldHydrateSelectedPlace, showSectors, types]);

    useEffect(() => {
        if (!corporation || data) return;
        setData(prev => ({...prev, city: corporation?.city ?? ""}))
    }, [corporation]);

    return (
        <>
            {(isLoadingPlaceData || isLoadingTypes || isLoadingSectors || isLoadingReverseGeocoding) && <Loader/>}
            <Card className={cardClassName}>
                {
                    showTitle &&
                    <Card.Header>
                        <Card.Title>
                            {title}
                        </Card.Title>
                    </Card.Header>
                }
                <Card.Body className="space-y-4">
                    <SentryRow>
                        <div className={"col-span-12 md:col-span-4 flex gap-2"}>
                            <SelectComponent
                                classNameWrapper="w-full"
                                id="place"
                                label="Local pré-cadastrado"
                                value={selectedPlace}
                                options={registeredAddress}
                                isDisabled={disabled}
                                onChange={handleRegisteredPlaceChange}
                            />
                            {actionInfo}
                        </div>
                        <SelectComponent
                            id="type_place"
                            label="Tipo"
                            value={types.find(address => address.value === data.type_place) ?? null}
                            options={types}
                            classNameWrapper="col-span-12 md:col-span-4"
                            isDisabled={disabled}
                            onChange={option => handleManualAddressChange("type_place", option?.value)}
                        />
                        <SelectComponent
                            loadOptions={getStreets}
                            value={optionsUtils.getValue(data.street)}
                            isAsync={true}
                            label={"Logradouro"}
                            onChange={option => handleManualAddressChange("street", option?.value)}
                            cacheOptions
                            defaultOptions={[]}
                            classNameWrapper="col-span-12 md:col-span-4"
                            isDisabled={disabled}
                        />
                    </SentryRow>
                    <SentryRow>
                        <InputForm
                            id="number"
                            label="Número"
                            value={data.number}
                            classNameWrapper="col-span-12 sm:col-span-3 lg:col-span-1"
                            disabled={disabled}
                            onChange={({target}) => handleManualAddressChange("number", target?.value ?? "")}
                        />
                        <SelectComponent
                            loadOptions={getNeighborhoods}
                            value={optionsUtils.getValue(data.neighborhood)}
                            isAsync={true}
                            label={"Bairro"}
                            onChange={selected => handleManualAddressChange("neighborhood", selected?.value ?? "")}
                            cacheOptions
                            defaultOptions={[]}
                            classNameWrapper="col-span-12 sm:col-span-9 lg:col-span-3"
                            isDisabled={disabled}
                        />
                        <SelectComponent
                            loadOptions={getStreets}
                            value={optionsUtils.getValue(data.crossroads)}
                            isAsync={true}
                            label={"Cruzamento"}
                            onChange={selected => handleManualAddressChange("crossroads", selected?.value ?? "", false)}
                            cacheOptions
                            defaultOptions={[]}
                            classNameWrapper="col-span-12 sm:col-span-6 lg:col-span-3"
                            isDisabled={disabled}
                        />
                        <InputForm
                            id="reference_point"
                            label="Ponto de referência"
                            value={data.reference_point}
                            classNameWrapper="col-span-12 sm:col-span-6 lg:col-span-5"
                            disabled={disabled}
                            onChange={({target}) => handleManualAddressChange("reference_point", target?.value ?? "", false)}
                        />
                    </SentryRow>
                    <SentryRow className="items-end">
                        <SelectComponent
                            loadOptions={getCities}
                            value={optionsUtils.getValue(data.city)}
                            isAsync={true}
                            label={"Município"}
                            onChange={selected => handleManualAddressChange("city", selected?.value ?? "")}
                            cacheOptions
                            defaultOptions={[]}
                            classNameWrapper={`col-span-12 ${showCoordinates ? "lg:col-span-7" : "lg:col-span-12"}`}
                            isDisabled={disabled}
                        />
                        {showCoordinates && (
                            <>
                                <InputForm
                                    id="latitude"
                                    label={`Latitude${coordinatesRequired ? " *" : ""}`}
                                    value={data.latitude}
                                    classNameWrapper="col-span-12 sm:col-span-6 lg:col-span-2"
                                    disabled={disabled}
                                    onChange={({target}) => handleManualAddressChange("latitude", target?.value ?? "")}
                                />
                                <InputForm
                                    id="longitude"
                                    label={`Longitude${coordinatesRequired ? " *" : ""}`}
                                    value={data.longitude}
                                    classNameWrapper="col-span-12 sm:col-span-6 lg:col-span-2"
                                    disabled={disabled}
                                    onChange={({target}) => handleManualAddressChange("longitude", target?.value ?? "")}
                                />
                            </>
                        )}
                        {showMap && (
                            <div className="col-span-12 lg:col-span-1">
                                <Button type="button" className={"w-full"} disabled={disabled}
                                        onClick={() => setShowModal(true)}>Mapa</Button>
                            </div>
                        )}
                    </SentryRow>
                    {showSectors && (
                        <SentryRow>
                            <SelectComponent
                                id="sectors"
                                label="Setor"
                                isMulti
                                wrapValues
                                options={sectorOptions}
                                value={getMultiOptionsByValue(sectorOptions, data.sectors)}
                                onChange={options => handleManualAddressChange("sectors", (options ?? []).map(option => option.value), false)}
                                classNameWrapper="col-span-12"
                                isDisabled={disabled}
                            />
                        </SentryRow>
                    )}

                    {
                        actionCopy &&
                        <div className={"flex justify-end"}>
                            {actionCopy}
                        </div>
                    }
                </Card.Body>
            </Card>

            <ModalReverseGeocoding
                open={showMap && showModal}
                setOpen={setShowModal}
                address={{
                    ...data,
                    address: data.street,
                    type: selectedType?.label ?? data.type_place,
                    sector: getDisplayValue(selectedSectors),
                    referencePoint: data.reference_point,
                }}
                setIsLoading={setIsLoadingReverseGeocoding}
                onConfirm={confirmLocation}
                showConfirmLocationAndAddress={showConfirmLocationAndAddress}
            />
        </>
    )
}

export default RegisteredLocation;
